import { useState, type JSX } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { AuthLayout } from '../app/AuthLayout';
import { useT } from '../i18n/useT';
import { twoFactorSession } from './twoFactorSession';
import { useCompleteLogin } from './useLoginFlow';

/** Has its own route so a refresh keeps the codes. */
export const RecoveryCodesScreen = (): JSX.Element => {
  const t = useT();
  const navigate = useNavigate();
  const completeLogin = useCompleteLogin();
  // Captured once: a later clear() (on success) must not bounce this screen back to login.
  const [session] = useState(() => twoFactorSession.get());
  if (session === null) {
    return <Navigate to="/login" replace />;
  }
  const codes = session.config?.recoveryCodes ?? [];

  const onContinue = (): void => {
    void (async () => {
      const access = session.access;
      if (access === undefined) {
        twoFactorSession.clear();
        await navigate('/login', { replace: true });
        return;
      }
      await completeLogin(access.token, access.tokenExpiryDate, session.returnUrl);
    })();
  };

  return (
    <AuthLayout>
      <h1 className="ow-heading">{t('Recovery codes')}</h1>
      <p className="ow-muted">
        {t(
          'Save these codes somewhere safe. You can use them if you lose access to your authenticator.'
        )}
      </p>
      {codes.length > 0 ? (
        <ul className="ow-codes">
          {codes.map((code) => (
            <li key={code}>{code}</li>
          ))}
        </ul>
      ) : (
        <p className="ow-muted">{t('No recovery codes available')}</p>
      )}
      <button type="button" className="ow-btn-primary" onClick={onContinue}>
        {t('Continue')}
      </button>
    </AuthLayout>
  );
};
